import React from 'react';
import { AlertTriangle, X } from 'lucide-react';

/**
 * Reusable confirmation dialog for destructive actions
 * @param {boolean} open - Whether dialog is open
 * @param {string} title - Dialog title
 * @param {string} message - Dialog message/description
 * @param {string} confirmText - Confirm button text
 * @param {string} cancelText - Cancel button text
 * @param {Function} onConfirm - Called when user confirms
 * @param {Function} onCancel - Called when user cancels
 * @param {boolean} danger - If true, shows red/danger styling
 * @param {boolean} loading - If true, shows loading state on confirm button
 */
export default function ConfirmDialog({
  open,
  title = "Confirm",
  message = "Are you sure?",
  confirmText = "Confirm",
  cancelText = "Cancel",
  onConfirm,
  onCancel,
  danger = false,
  loading = false,
  icon: Icon = AlertTriangle,
}) {
  // Handle ESC key
  React.useEffect(() => {
    if (!open) return;
    const handleEsc = (e) => {
      if (e.key === 'Escape' && onCancel) onCancel();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [open, onCancel]);

  // Prevent body scroll when open
  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [open]);

  if (!open) return null;

  return (
    <div 
      className="confirm-dialog-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && onCancel) onCancel();
      }}
    >
      <div className="confirm-dialog-card">
        {/* Close button */}
        <button 
          className="confirm-dialog-close"
          onClick={onCancel}
          aria-label="Close"
        >
          <X size={20} />
        </button>

        {/* Icon */}
        <div className={`confirm-dialog-icon ${danger ? 'danger' : 'warning'}`}>
          <Icon size={28} />
        </div>

        {/* Content */}
        <h2 id="confirm-dialog-title" className="confirm-dialog-title">
          {title}
        </h2>
        <p className="confirm-dialog-message">
          {message}
        </p>

        {/* Actions */}
        <div className="confirm-dialog-actions">
          <button 
            className="confirm-dialog-btn cancel"
            onClick={onCancel}
            disabled={loading}
          >
            {cancelText}
          </button>
          <button 
            className={`confirm-dialog-btn confirm ${danger ? 'danger' : 'primary'}`}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? (
              <span className="confirm-dialog-spinner" />
            ) : null}
            {confirmText}
          </button>
        </div>
      </div>

      <style>{`
        .confirm-dialog-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 16px;
          animation: fadeIn 0.15s ease-out;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideUp {
          from { transform: translateY(20px) scale(0.95); opacity: 0; }
          to { transform: translateY(0) scale(1); opacity: 1; }
        }

        .confirm-dialog-card {
          width: min(400px, calc(100% - 32px));
          background: #fff;
          border-radius: 20px;
          padding: 24px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
          position: relative;
          animation: slideUp 0.2s ease-out;
        }

        html.dark .confirm-dialog-card {
          background: #1e293b;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
        }

        .confirm-dialog-close {
          position: absolute;
          top: 12px;
          right: 12px;
          width: 32px;
          height: 32px;
          border: none;
          background: transparent;
          border-radius: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #94a3b8;
          transition: all 0.2s;
        }

        [dir="rtl"] .confirm-dialog-close {
          right: auto;
          left: 12px;
        }

        .confirm-dialog-close:hover {
          background: rgba(0, 0, 0, 0.05);
          color: #64748b;
        }

        html.dark .confirm-dialog-close:hover {
          background: rgba(255, 255, 255, 0.1);
          color: #e2e8f0;
        }

        .confirm-dialog-icon {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 16px;
        }

        .confirm-dialog-icon.warning {
          background: #fef3c7;
          color: #d97706;
        }

        .confirm-dialog-icon.danger {
          background: #fee2e2;
          color: #dc2626;
        }

        html.dark .confirm-dialog-icon.warning {
          background: rgba(217, 119, 6, 0.2);
        }

        html.dark .confirm-dialog-icon.danger {
          background: rgba(220, 38, 38, 0.2);
        }

        .confirm-dialog-title {
          font-size: 18px;
          font-weight: 700;
          color: #1e293b;
          text-align: center;
          margin: 0 0 8px 0;
        }

        html.dark .confirm-dialog-title {
          color: #f1f5f9;
        }

        .confirm-dialog-message {
          font-size: 14px;
          color: #64748b;
          text-align: center;
          line-height: 1.5;
          margin: 0 0 24px 0;
        }

        html.dark .confirm-dialog-message {
          color: #94a3b8;
        }

        .confirm-dialog-actions {
          display: flex;
          gap: 12px;
        }

        .confirm-dialog-btn {
          flex: 1;
          padding: 12px 16px;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .confirm-dialog-btn.cancel {
          background: #f1f5f9;
          border: 1px solid #e2e8f0;
          color: #475569;
        }

        .confirm-dialog-btn.cancel:hover:not(:disabled) {
          background: #e2e8f0;
        }

        html.dark .confirm-dialog-btn.cancel {
          background: #334155;
          border-color: #475569;
          color: #e2e8f0;
        }

        html.dark .confirm-dialog-btn.cancel:hover:not(:disabled) {
          background: #475569;
        }

        .confirm-dialog-btn.confirm.primary {
          background: linear-gradient(135deg, #22c55e 0%, #1F6F3A 100%);
          border: none;
          color: white;
        }

        .confirm-dialog-btn.confirm.primary:hover:not(:disabled) {
          box-shadow: 0 4px 12px rgba(34, 197, 94, 0.3);
          transform: translateY(-1px);
        }

        .confirm-dialog-btn.confirm.danger {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          border: none;
          color: white;
        }

        .confirm-dialog-btn.confirm.danger:hover:not(:disabled) {
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
          transform: translateY(-1px);
        }

        .confirm-dialog-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none !important;
        }

        .confirm-dialog-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
